// Rytmus svozů a navrhované svozy (01 „rytmus svozů“, 10. 10. 2026; kontrakt `spec_RS.py`, akce `svoz_rytmus.*`):
//   GET  /svozy/navrhy?stav&spolehlivost&hledat&dni&zakaznik_id   navrhované svozy s předvyplněním požadavku a `muze`
//   GET  /zakaznici/{id}/rytmus-svozu                              rytmus zákazníka a provozoven (karta)
//   GET  /svozy/sezonnost                                          sezónnost odpadů po měsících (celá firma)
//   POST /svozy/navrhy/odlozit { zakaznik_id, provozovna_id?, do_dne?, duvod }   odložit s důvodem (vyřeší signál)
//   POST /svozy/navrhy/odlozeni/{id}/zrusit                        zrušit odložení (Zpět)
// Všechno počítá server (intervaly, termíny, množství); obrazovky jen zobrazují. Požadavek z návrhu se zakládá
// běžným `POST /pozadavky-svozu` s `predvyplneni` návrhu.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, type QueryClient, useQuery } from '@tanstack/react-query';
import { api } from './klient';
import { KLIC_SVOZ, type PozadavekSvozu, zalozitPozadavek } from './svoz-pozadavky';

export type NavrhySvozu = Schema<'NavrhySvozu'>;
export type NavrhSvozu = Schema<'NavrhSvozu'>;
export type RytmusSvozuJednotka = Schema<'RytmusSvozuJednotka'>;
export type RytmusSvozuZakaznika = Schema<'RytmusSvozuZakaznika'>;
export type SezonnostOdpadu = Schema<'SezonnostOdpadu'>;
export type OdlozeniSvozu = Schema<'OdlozeniSvozu'>;
export type StavNavrhu = RytmusSvozuJednotka['stav'];

export const AKCE_RYTMUS_CIST = 'svoz_rytmus.cist';
export const AKCE_RYTMUS_ODLOZIT = 'svoz_rytmus.odlozit';

export const KLIC_RYTMUS = ['rytmus-svozu'] as const;

export type FiltrStavu = 'k_reseni' | 'po_terminu' | 'blizi_se' | 'odlozeno' | 'vse';

export const FILTRY_STAVU: readonly { klic: FiltrStavu; nazev: string }[] = [
  { klic: 'k_reseni', nazev: 'K řešení' },
  { klic: 'po_terminu', nazev: 'Po termínu' },
  { klic: 'blizi_se', nazev: 'Blíží se' },
  { klic: 'odlozeno', nazev: 'Odložené' },
  { klic: 'vse', nazev: 'Vše v okně' },
];

/** Štítek stavu podle 04 (barvy jen z tokenů). */
export const STITEK_STAVU: Readonly<Record<StavNavrhu, string>> = {
  po_terminu: 'stitek--danger',
  blizi_se: 'stitek--warn',
  naplanovano: 'stitek--ok',
  odlozeno: 'stitek--neutral',
  v_rytmu: 'stitek--info',
  odmlka: 'stitek--neutral',
  bez_rytmu: 'stitek--neutral',
};

export const MESICE_ZKRATKY = [
  'led',
  'úno',
  'bře',
  'dub',
  'kvě',
  'čvn',
  'čvc',
  'srp',
  'zář',
  'říj',
  'lis',
  'pro',
];

export interface FiltrNavrhu {
  stav: FiltrStavu;
  vse: boolean;
  hledat: string;
  zakaznikId?: string;
}

export function useNavrhySvozu(f: FiltrNavrhu, povoleno = true) {
  const q = new URLSearchParams();
  if (f.stav !== 'k_reseni') q.set('stav', f.stav);
  if (f.vse) q.set('spolehlivost', 'vse');
  if (f.hledat.trim()) q.set('hledat', f.hledat.trim());
  if (f.zakaznikId) q.set('zakaznik_id', f.zakaznikId);
  const dotaz = q.toString();
  return useQuery({
    queryKey: [...KLIC_RYTMUS, 'navrhy', dotaz],
    queryFn: ({ signal }) =>
      api.get<NavrhySvozu>(`/svozy/navrhy${dotaz ? `?${dotaz}` : ''}`, { signal }),
    enabled: povoleno,
    placeholderData: keepPreviousData,
  });
}

export function useRytmusZakaznika(zakaznikId: string | undefined, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_RYTMUS, 'zakaznik', zakaznikId],
    queryFn: ({ signal }) =>
      api.get<RytmusSvozuZakaznika>(
        `/zakaznici/${encodeURIComponent(zakaznikId ?? '')}/rytmus-svozu`,
        { signal },
      ),
    enabled: Boolean(zakaznikId) && povoleno,
    retry: false,
  });
}

export function useSezonnostOdpadu(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_RYTMUS, 'sezonnost'],
    queryFn: ({ signal }) => api.get<SezonnostOdpadu>('/svozy/sezonnost?limit=12', { signal }),
    enabled: povoleno,
    staleTime: 10 * 60_000,
  });
}

/** Po zápisu: návrhy, rytmus karet, požadavky a zákazníci (štítek „po termínu“). */
export function obnovitRytmus(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: KLIC_RYTMUS });
  void queryClient.invalidateQueries({ queryKey: KLIC_SVOZ });
  void queryClient.invalidateQueries({ queryKey: ['zakaznici'] });
}

export const odlozitSvoz = (telo: {
  zakaznik_id: string;
  provozovna_id?: string | null;
  do_dne?: string;
  duvod: string;
}) => api.post<OdlozeniSvozu>('/svozy/navrhy/odlozit', telo);

export const zrusitOdlozeni = (id: string) =>
  api.post<{ id: string; zruseno: boolean }>(
    `/svozy/navrhy/odlozeni/${encodeURIComponent(id)}/zrusit`,
  );

/** Požadavek na svoz z předvyplnění návrhu (stejná cesta jako „+ Požádat o svoz“). */
export function pozadavekZNavrhu(
  p: NonNullable<RytmusSvozuJednotka['predvyplneni']>,
): Promise<PozadavekSvozu> {
  return zalozitPozadavek({
    odkud_zakaznik_id: p.odkud_zakaznik_id,
    ...(p.odkud_provozovna_id ? { odkud_provozovna_id: p.odkud_provozovna_id } : {}),
    termin: p.termin,
    // Pravidelný svoz z ručního intervalu provozovny (05/4190): zakázka z požadavku bude „↻ pravidelný svoz“.
    ...(p.pravidelny && p.odkud_provozovna_id ? { pravidelny_svoz: true } : {}),
    polozky: p.polozky.map((r) => ({
      polozka_id: r.polozka_id,
      mnozstvi: r.mnozstvi ?? null,
      mj: r.mnozstvi ? (r.mj ?? 'kg') : null,
    })),
  });
}

/** „za 3 dny“, „dnes“, „po termínu 5 dní“. */
export function popisTerminuNavrhu(dni: number | null | undefined): string {
  if (dni === null || dni === undefined) return '';
  if (dni === 0) return 'dnes';
  const a = Math.abs(dni);
  const slovo = a === 1 ? 'den' : a >= 2 && a <= 4 ? 'dny' : 'dní';
  const slovoZa = a === 1 ? 'den' : a >= 2 && a <= 4 ? 'dny' : 'dní';
  return dni > 0 ? `za ${a} ${slovoZa}` : `po termínu ${a} ${slovo}`;
}
